import * as dateFns from "date-fns";

import { Constants, getRunningTimeMinutes } from "@eagerdog/constants";
import {
  IEvent,
  IShow,
  IScoreRegistrationSummary,
  IWyip3bScheduleDay,
  IWyip3bScheduleColumn,
  IWyip3bScheduleItem,
  IWyip3bScheduleDogEntry,
} from "@eagerdog/interfaces";

/**
 * One-off, event-specific "interwoven" judging schedule computation for event
 * handle "wyip3b" (UKC Nosework). Extracted out of the DOCX judging-schedule
 * builder so the exact same computed order/timing/dog-lists can be consumed
 * by BOTH the DOCX export (packages/modules/forms) and a plain JSON endpoint
 * for the mobile app's Schedule/Run Order tabs — with no risk of the two
 * silently drifting apart.
 *
 * The secretary for this event wants classes from both trials on a given day
 * re-sequenced into AM/PM judge blocks, instead of the standard sequential
 * per-show layout. That exact re-sequencing (which elements pair into which
 * session, and how Trial 1 / Trial 2 interleave within a level) is a manual
 * choice made by the secretary, not something derivable from a general rule —
 * so it's captured here as an explicit config rather than an algorithm.
 *
 * Judge names, entry counts, and start times are all resolved/computed live
 * from the show data at call time (using the same per-entry running-time
 * table as the standard UKC Nosework schedule), so this can't silently drift
 * out of sync with the DB — only the ORDER is hardcoded.
 *
 * Deliberately kept as its own config, entirely separate from the standard
 * judging-schedule/ring/running-order model, so it can be freely tuned for
 * this one customer without any risk to the standard export or to any other
 * event's stored rings/running order.
 */

export const WYIP3B_INTERWOVEN_EVENT_HANDLE = "wyip3b";

const LUNCH_DURATION_MINUTES = 60;

// Short break inserted between levels (not between elements within a level)
// to give the secretary time to reposition/move cars between rounds. Defined
// as unordered adjacent pairs (rather than a directional "entering X after Y"
// map) so the same breaks still apply regardless of which direction a given
// day's session runs the levels in — e.g. a day running Novice->Elite gets a
// break Advanced->Superior, and a day running Elite->Novice (reversed) gets
// that same break Superior->Advanced. Novice<->Advanced never gets a break.
const LEVEL_BREAK_DURATION_MINUTES = 10;
const LEVEL_BREAK_PAIRS: [string, string][] = [
  ["Advanced", "Superior"],
  ["Superior", "Master"],
  ["Master", "Elite"],
];

function isLevelBreakBoundary(levelA: string, levelB: string): boolean {
  return LEVEL_BREAK_PAIRS.some(
    ([x, y]) => (x === levelA && y === levelB) || (x === levelB && y === levelA)
  );
}

interface IScheduleLineItem {
  level: string;
  element: string;
  trials: (1 | 2)[];
}

interface IJudgeColumnConfig {
  judgeLabel: string;
  amItems: IScheduleLineItem[];
  pmItems: IScheduleLineItem[];
}

interface IDayPageConfig {
  dayLabel: string;
  columns: IJudgeColumnConfig[];
}

const SCHEDULE_CONFIG: IDayPageConfig[] = [
  {
    dayLabel: "Friday",
    columns: [
      {
        judgeLabel: "Lisa",
        amItems: [
          { level: "Novice", element: "Vehicle", trials: [1, 2] },
          { level: "Novice", element: "Exterior", trials: [1, 2] },
          { level: "Advanced", element: "Vehicle", trials: [1, 2] },
          { level: "Advanced", element: "Exterior", trials: [1, 2] },
          { level: "Superior", element: "Vehicle", trials: [1] },
          { level: "Superior", element: "Exterior", trials: [1] },
          { level: "Superior", element: "Exterior", trials: [2] },
          { level: "Superior", element: "Vehicle", trials: [2] },
          { level: "Master", element: "Vehicle", trials: [1] },
          { level: "Master", element: "Exterior", trials: [1] },
          { level: "Master", element: "Exterior", trials: [2] },
          { level: "Master", element: "Vehicle", trials: [2] },
        ],
        pmItems: [
          { level: "Novice", element: "Interior", trials: [1, 2] },
          { level: "Novice", element: "Container", trials: [1, 2] },
          { level: "Advanced", element: "Interior", trials: [1, 2] },
          { level: "Advanced", element: "Container", trials: [1, 2] },
          { level: "Superior", element: "Interior", trials: [1] },
          { level: "Superior", element: "Container", trials: [1] },
          { level: "Superior", element: "Interior", trials: [2] },
          { level: "Superior", element: "Container", trials: [2] },
          { level: "Master", element: "Interior", trials: [1] },
          { level: "Master", element: "Container", trials: [1] },
          { level: "Master", element: "Interior", trials: [2] },
          { level: "Master", element: "Container", trials: [2] },
        ],
      },
    ],
  },
  {
    dayLabel: "Saturday",
    columns: [
      {
        // Secretary-requested swap (2026-09-04): Lisa's AM/PM element blocks
        // are flipped from the original config - Exterior now runs AM,
        // Container now runs PM. Internal level/trial ordering within each
        // block is unchanged, only which session (AM vs PM) it runs in.
        judgeLabel: "Lisa",
        amItems: [
          { level: "Novice", element: "Exterior", trials: [1, 2] },
          { level: "Advanced", element: "Exterior", trials: [1, 2] },
          { level: "Superior", element: "Exterior", trials: [1] },
          { level: "Superior", element: "Exterior", trials: [2] },
          { level: "Master", element: "Exterior", trials: [1] },
          { level: "Master", element: "Exterior", trials: [2] },
        ],
        pmItems: [
          { level: "Novice", element: "Container", trials: [1, 2] },
          { level: "Advanced", element: "Container", trials: [1, 2] },
          { level: "Superior", element: "Container", trials: [1] },
          { level: "Superior", element: "Container", trials: [2] },
          { level: "Master", element: "Container", trials: [1] },
          { level: "Master", element: "Container", trials: [2] },
        ],
      },
      {
        judgeLabel: "Sandra",
        amItems: [
          { level: "Novice", element: "Vehicle", trials: [1, 2] },
          { level: "Advanced", element: "Vehicle", trials: [1, 2] },
          { level: "Superior", element: "Vehicle", trials: [1] },
          { level: "Superior", element: "Vehicle", trials: [2] },
          { level: "Master", element: "Vehicle", trials: [1] },
          { level: "Master", element: "Vehicle", trials: [2] },
        ],
        pmItems: [
          { level: "Novice", element: "Interior", trials: [1, 2] },
          { level: "Advanced", element: "Interior", trials: [1, 2] },
          { level: "Superior", element: "Interior", trials: [1] },
          { level: "Superior", element: "Interior", trials: [2] },
          { level: "Master", element: "Interior", trials: [1] },
          { level: "Master", element: "Interior", trials: [2] },
        ],
      },
    ],
  },
  {
    dayLabel: "Sunday",
    columns: [
      {
        // Secretary-requested swap (2026-09-04): Sandra's AM/PM element blocks
        // are flipped from the original config - Exterior now runs AM,
        // Container now runs PM. Internal level/trial ordering within each
        // block is unchanged, only which session (AM vs PM) it runs in.
        //
        // Secretary-requested reversal (2026-09-06): the level order within
        // each session is reversed (Elite first, Novice last) so Sunday runs
        // "backwards" relative to Friday/Saturday. Trial 1 still runs before
        // Trial 2 within each level — only the level grouping order flips.
        judgeLabel: "Sandra",
        amItems: [
          { level: "Elite", element: "Exterior", trials: [1] },
          { level: "Elite", element: "Exterior", trials: [2] },
          { level: "Master", element: "Exterior", trials: [1] },
          { level: "Master", element: "Exterior", trials: [2] },
          { level: "Superior", element: "Exterior", trials: [1] },
          { level: "Superior", element: "Exterior", trials: [2] },
          { level: "Advanced", element: "Exterior", trials: [1, 2] },
          { level: "Novice", element: "Exterior", trials: [1, 2] },
        ],
        pmItems: [
          { level: "Elite", element: "Container", trials: [1] },
          { level: "Elite", element: "Container", trials: [2] },
          { level: "Master", element: "Container", trials: [1] },
          { level: "Master", element: "Container", trials: [2] },
          { level: "Superior", element: "Container", trials: [1] },
          { level: "Superior", element: "Container", trials: [2] },
          { level: "Advanced", element: "Container", trials: [1, 2] },
          { level: "Novice", element: "Container", trials: [1, 2] },
        ],
      },
      {
        // Secretary-requested reversal (2026-09-06): see comment on Sandra's
        // column above — same reversed level order, Trial 1 before Trial 2
        // preserved within each level.
        judgeLabel: "Lisa",
        amItems: [
          { level: "Elite", element: "Vehicle", trials: [1] },
          { level: "Elite", element: "Vehicle", trials: [2] },
          { level: "Master", element: "Vehicle", trials: [1] },
          { level: "Master", element: "Vehicle", trials: [2] },
          { level: "Superior", element: "Vehicle", trials: [1] },
          { level: "Superior", element: "Vehicle", trials: [2] },
          { level: "Advanced", element: "Vehicle", trials: [1, 2] },
          { level: "Novice", element: "Vehicle", trials: [1, 2] },
        ],
        pmItems: [
          { level: "Elite", element: "Interior", trials: [1] },
          { level: "Elite", element: "Interior", trials: [2] },
          { level: "Master", element: "Interior", trials: [1] },
          { level: "Master", element: "Interior", trials: [2] },
          { level: "Superior", element: "Interior", trials: [1] },
          { level: "Superior", element: "Interior", trials: [2] },
          { level: "Advanced", element: "Interior", trials: [1, 2] },
          { level: "Novice", element: "Interior", trials: [1, 2] },
        ],
      },
    ],
  },
];

interface IInternalScheduledItem extends IScheduleLineItem {
  startTime: Date;
  // One group per trial in this item, in trial order (Trial 1's dogs before
  // Trial 2's), each already sorted by armband number.
  dogGroups: IScoreRegistrationSummary[][];
  breakBeforeMinutes?: number;
}

// ---------------
// Groups this event's shows by calendar day (in the event's timezone), sorted
// chronologically, and zips the 1st/2nd/3rd distinct day found against the 1st/
// 2nd/3rd day in SCHEDULE_CONFIG (Friday/Saturday/Sunday). Within a day, the
// earlier show_date is Trial 1 and the later (trailing) show_date is Trial 2 —
// consistent with how trailing shows are placeholder-dated one minute later.
function groupShowsByDay(event: IEvent, shows: IShow[]): Map<string, [IShow, IShow]> {
  const timeZone = event.timezone || "America/Toronto";

  const dateKeyFor = (show: IShow) =>
    new Intl.DateTimeFormat("en-US", {
      dateStyle: "short",
      timeZone,
    }).format(new Date(show.show_date));

  const byDateKey = new Map<string, IShow[]>();
  for (const show of shows) {
    const key = dateKeyFor(show);
    const existing = byDateKey.get(key);
    if (existing) {
      existing.push(show);
    } else {
      byDateKey.set(key, [show]);
    }
  }

  const orderedDayKeys = Array.from(byDateKey.keys()).sort(
    (a, b) =>
      new Date(byDateKey.get(a)![0].show_date).getTime() -
      new Date(byDateKey.get(b)![0].show_date).getTime()
  );

  const result = new Map<string, [IShow, IShow]>();

  orderedDayKeys.forEach((key, idx) => {
    const dayConfig = SCHEDULE_CONFIG[idx];
    if (!dayConfig) return;

    const showsForDay = byDateKey.get(key)!;
    if (showsForDay.length < 2) return;

    const [trial1, trial2] = [...showsForDay].sort(
      (a, b) => new Date(a.show_date).getTime() - new Date(b.show_date).getTime()
    );

    result.set(dayConfig.dayLabel, [trial1, trial2]);
  });

  return result;
}

// ---------------
const compareArmbands = (a: IScoreRegistrationSummary, b: IScoreRegistrationSummary): number => {
  const getArmbandNum = (value?: string) => {
    const parsed = parseInt((value || "").replace(/[^0-9]/g, ""), 10);
    return Number.isFinite(parsed) ? parsed : Number.MAX_SAFE_INTEGER;
  };

  const aArm = a.arm_number || "";
  const bArm = b.arm_number || "";
  const aNum = getArmbandNum(aArm);
  const bNum = getArmbandNum(bArm);

  if (aNum !== bNum) return aNum - bNum;
  return aArm.localeCompare(bArm, undefined, { numeric: true });
};

// ---------------
// Mirrors getFormattedArmNumber in packages/modules/forms/src/util/score-util.ts
// so the printed/displayed armband is identical between the DOCX export and
// the JSON endpoint. Kept as a local copy rather than a cross-package import
// so this package stays a small, dependency-light pure-computation library.
function formatArmNumber(score: IScoreRegistrationSummary): string {
  let armNumber = score.arm_number;
  if (armNumber) {
    armNumber = score.dog_class?.level?.toLowerCase()?.includes("preferred")
      ? `P${armNumber}`
      : armNumber;

    if (
      score.show_type === Constants.show_type.conformation &&
      (score as any).competing_owner_handler === true
    ) {
      armNumber = `*${armNumber}`;
    }
  }

  return armNumber || "";
}

// ---------------
// Returns one sorted-by-armband group of dogs per trial in this item, in
// trial order (Trial 1's dogs listed before Trial 2's, per the secretary's
// request — even when both trials share a single "Trial 1 and 2" line).
function dogGroupsFor(
  item: IScheduleLineItem,
  shows: [IShow, IShow],
  scores: IScoreRegistrationSummary[]
): IScoreRegistrationSummary[][] {
  return item.trials.map((trial) => {
    const show = shows[trial - 1];
    return scores
      .filter(
        (score) =>
          JSON.stringify(score.show_id) === JSON.stringify(show._id) &&
          score.dog_class?.show_element === item.element &&
          score.dog_class?.level === item.level
      )
      .sort(compareArmbands);
  });
}

// ---------------
// Walks a judge's ordered list of line items starting at sessionStart, using
// the same per-entry running-time table as the standard UKC Nosework schedule
// to advance the clock after each item.
function scheduleSession(
  event: IEvent,
  items: IScheduleLineItem[],
  sessionStart: Date,
  shows: [IShow, IShow],
  scores: IScoreRegistrationSummary[]
): { entries: IInternalScheduledItem[]; endTime: Date } {
  let clock = new Date(sessionStart);
  const entries: IInternalScheduledItem[] = [];
  let previousLevel: string | undefined;

  for (const item of items) {
    let breakBeforeMinutes: number | undefined;
    if (previousLevel && previousLevel !== item.level && isLevelBreakBoundary(previousLevel, item.level)) {
      clock = dateFns.addMinutes(clock, LEVEL_BREAK_DURATION_MINUTES);
      breakBeforeMinutes = LEVEL_BREAK_DURATION_MINUTES;
    }
    previousLevel = item.level;

    const dogGroups = dogGroupsFor(item, shows, scores);
    const entryCount = dogGroups.reduce((sum, group) => sum + group.length, 0);

    entries.push({
      ...item,
      startTime: new Date(clock),
      dogGroups,
      breakBeforeMinutes,
    });

    const perEntryMinutes = getRunningTimeMinutes(
      event.sanctioning_club,
      Constants.show_type.nosework,
      item.element,
      item.level
    );

    clock = dateFns.addMinutes(clock, perEntryMinutes * entryCount);
  }

  return { entries, endTime: clock };
}

// ---------------
// Resolves the real judge name (and judge number, when available) for a column
// from the show's actual show_elements data (rather than trusting the config's
// judgeLabel), so the displayed/printed name always matches whoever is actually
// assigned in the system.
function resolveJudgeName(
  column: IJudgeColumnConfig,
  shows: [IShow, IShow]
): { name: string; number?: string } {
  const firstItem = column.amItems[0] || column.pmItems[0];
  if (!firstItem) return { name: column.judgeLabel };

  const show = shows[firstItem.trials[0] - 1];
  const matchingElement = (show.show_elements || []).find(
    (el: any) => el.show_element === firstItem.element && el.level === firstItem.level
  );

  const name = (matchingElement?.judge_name || column.judgeLabel).trim();
  const number = matchingElement?.judge_number
    ? String(matchingElement.judge_number).trim()
    : undefined;

  return { name, number };
}

// ---------------
function buildDogEntries(entry: IInternalScheduledItem): IWyip3bScheduleDogEntry[] {
  // For the combined "Trial 1 and 2" lines, flag dogs (by dog_id) that only
  // appear in one of the two trial groups for this class — the secretary
  // wants those called out even though the dog may have other runs elsewhere.
  const isCombined = entry.trials.length === 2;
  const groupDogIdSets = isCombined
    ? entry.dogGroups.map((dogs) => new Set(dogs.map((dcs) => JSON.stringify(dcs.dog_id))))
    : [];

  const dogs: IWyip3bScheduleDogEntry[] = [];

  entry.dogGroups.forEach((group, groupIdx) => {
    const trial = entry.trials[groupIdx];
    const otherGroupIds = isCombined ? groupDogIdSets[1 - groupIdx] : undefined;

    group.forEach((score) => {
      const soloTrialAsterisk =
        !!otherGroupIds && !otherGroupIds.has(JSON.stringify(score.dog_id));

      dogs.push({
        dog_id: String(score.dog_id),
        arm_number: formatArmNumber(score),
        breed: score.breed,
        call_name: score.call_name,
        score_id: String(score._id),
        show_id: String(score.show_id),
        trial,
        solo_trial_asterisk: soloTrialAsterisk,
        checked_in: !!score.checked_in,
        completed: score.status === Constants.score_status.complete || !!score.complete,
        scratched: score.status === Constants.score_status.scratched,
        conflict: score.status === Constants.score_status.conflict || !!(score as any).conflict,
      });
    });
  });

  return dogs;
}

// ---------------
function toScheduleItem(entry: IInternalScheduledItem): IWyip3bScheduleItem {
  const trialLabel = entry.trials.length === 2 ? "Trial 1 & Trial 2" : `Trial ${entry.trials[0]}`;
  const entryCount = entry.dogGroups.reduce((sum, group) => sum + group.length, 0);

  return {
    level: entry.level,
    element: entry.element,
    trials: entry.trials,
    trial_label: trialLabel,
    start_time: entry.startTime.toISOString(),
    entry_count: entryCount,
    break_before_minutes: entry.breakBeforeMinutes,
    dogs: buildDogEntries(entry),
  };
}

// ---------------
/**
 * Computes the full interwoven schedule (all configured days/columns/sessions)
 * for this event, live from the given shows + scores. Days with no matching
 * pair of trials found are returned with an empty `columns` array.
 */
export function computeWyip3bInterwovenSchedule(
  event: IEvent,
  shows: IShow[],
  scores: IScoreRegistrationSummary[]
): IWyip3bScheduleDay[] {
  const dayShows = groupShowsByDay(event, shows);

  return SCHEDULE_CONFIG.map((dayConfig): IWyip3bScheduleDay => {
    const showsForDay = dayShows.get(dayConfig.dayLabel);
    if (!showsForDay) {
      return { day_label: dayConfig.dayLabel, columns: [] };
    }

    const [trial1Show, trial2Show] = showsForDay;
    const amStart = new Date(trial1Show.show_date);

    const amSchedules = dayConfig.columns.map((c) =>
      scheduleSession(event, c.amItems, amStart, showsForDay, scores)
    );
    const amEnd = new Date(Math.max(...amSchedules.map((s) => s.endTime.getTime())));
    const pmStart = dateFns.addMinutes(amEnd, LUNCH_DURATION_MINUTES);
    const pmSchedules = dayConfig.columns.map((c) =>
      scheduleSession(event, c.pmItems, pmStart, showsForDay, scores)
    );

    const columns: IWyip3bScheduleColumn[] = dayConfig.columns.map((columnConfig, idx) => {
      const judge = resolveJudgeName(columnConfig, showsForDay);
      return {
        judge_label: columnConfig.judgeLabel,
        judge_name: judge.name,
        judge_number: judge.number,
        am_items: amSchedules[idx].entries.map(toScheduleItem),
        pm_items: pmSchedules[idx].entries.map(toScheduleItem),
      };
    });

    return {
      day_label: dayConfig.dayLabel,
      date: amStart.toISOString(),
      am_end_time: amEnd.toISOString(),
      pm_start_time: pmStart.toISOString(),
      trial_1_show_id: String(trial1Show._id),
      trial_1_show_status: trial1Show.show_status,
      trial_1_show_status_note: trial1Show.show_status_note,
      trial_2_show_id: String(trial2Show._id),
      trial_2_show_status: trial2Show.show_status,
      trial_2_show_status_note: trial2Show.show_status_note,
      columns,
    };
  });
}
